<template>
  <div class="growth-chart" data-cy="membership-growth-chart">
    <header class="growth-chart__head">
      <div class="growth-chart__heading">
        <span class="growth-chart__eyebrow">Trailing 12 Months</span>
        <div class="growth-chart__title-row">
          <h3 class="growth-chart__title">Membership Growth</h3>
          <Tooltip placement="top" :width="280">
            <template #trigger>
              <i
                class="fas fa-info-circle growth-chart__info"
                aria-label="More information"
              ></i>
            </template>
            <template #tooltip>
              <span class="growth-chart__tooltip-text">{{ TOOLTIP }}</span>
            </template>
          </Tooltip>
          <Tooltip
            v-if="showGrowthPill"
            placement="top"
            :width="240"
            class="growth-chart__growth-pill-wrap"
          >
            <template #trigger>
              <span
                class="growth-chart__growth-pill"
                :class="`growth-chart__growth-pill--${growthTone}`"
                data-cy="growth-chart-12-mo-pill"
              >
                {{ growthLabel }}
              </span>
            </template>
            <template #tooltip>
              <span class="growth-chart__tooltip-text">
                {{ growthTooltipText }}
              </span>
            </template>
          </Tooltip>
        </div>
      </div>
      <ul class="growth-chart__legend" aria-label="Chart legend">
        <li
          v-for="entry in legendEntries"
          :key="entry.label"
          class="growth-chart__legend-item"
        >
          <span
            class="growth-chart__swatch"
            :class="`growth-chart__swatch--${entry.shape}`"
            :style="{ backgroundColor: entry.color }"
            aria-hidden="true"
          ></span>
          <span class="growth-chart__legend-label">{{ entry.label }}</span>
        </li>
      </ul>
    </header>

    <div class="growth-chart__body">
      <Skeleton
        v-if="showSkeleton"
        width="100%"
        height="16rem"
        data-cy="membership-growth-chart-skeleton"
      />
      <div v-else class="growth-chart__canvas-wrap">
        <Chart
          type="bar"
          :labels="labels"
          :series="series"
          :options="options"
          :format-y-axis="formatYTick"
          @point-click="handlePointClick"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import moment from "moment";
import { computed, onMounted } from "vue";
import { useStore } from "vuex";

import Chart from "@/components/Chart/Chart.vue";
import Tooltip from "@/components/Tooltip.vue";
import Skeleton from "./Skeleton.vue";
import type { MembershipGrowthChartBucket } from "./types";

const TOOLTIP =
  "Active members each month with adds and losses bars. Click a bar to drill into the Members report for that month.";

const ACTIVE_LINE_COLOR = "#355C8A";
const NEW_BAR_COLOR = "#1C64F2";
const LOST_BAR_COLOR = "#9CA3AF";

const legendEntries = [
  { label: "Active", color: ACTIVE_LINE_COLOR, shape: "line" as const },
  { label: "Adds", color: NEW_BAR_COLOR, shape: "bar" as const },
  { label: "Losses", color: LOST_BAR_COLOR, shape: "bar" as const }
];

const store = useStore();

const buckets = computed<MembershipGrowthChartBucket[] | null>(
  () => store.state.membershipDashboardGrowthChart
);
const loading = computed<boolean>(
  () => store.state.membershipDashboardGrowthChartLoading
);
const showSkeleton = computed<boolean>(
  () => loading.value || buckets.value === null
);

const selectedProgramIds = computed<number[]>(
  () => store.state.membershipDashboardFilters.membership_program_ids ?? []
);

// Trailing-12-month change: oldest bucket vs current bucket. Suppressed when
// the oldest bucket is 0 (tenant didn't exist 12 months ago) so we don't
// render a meaningless +∞% / divide-by-zero pill.
const oldestBucket = computed<MembershipGrowthChartBucket | null>(() => {
  const data = buckets.value;
  if (!data || data.length < 2) return null;
  return data[0] ?? null;
});

const newestBucket = computed<MembershipGrowthChartBucket | null>(() => {
  const data = buckets.value;
  if (!data || data.length === 0) return null;
  return data.at(-1) ?? null;
});

const growthPercent = computed<number>(() => {
  const oldest = oldestBucket.value?.active_members ?? 0;
  const newest = newestBucket.value?.active_members ?? 0;
  if (oldest === 0) return 0;
  return ((newest - oldest) / oldest) * 100;
});

const growthLabel = computed<string>(() => {
  const pct = growthPercent.value;
  if (pct > 0) return `+${pct.toFixed(1)}% ↑`;
  if (pct < 0) return `−${Math.abs(pct).toFixed(1)}% ↓`;
  return "0.0% →";
});

const growthTone = computed<"positive" | "negative" | "neutral">(() => {
  const pct = growthPercent.value;
  if (pct > 0) return "positive";
  if (pct < 0) return "negative";
  return "neutral";
});

const growthTooltipText = computed<string>(() => {
  const oldest = oldestBucket.value;
  if (!oldest) return "";
  const monthLabel = moment(oldest.month, "YYYY-MM").format("MMMM YYYY");
  return `Was ${oldest.active_members.toLocaleString(
    "en-US"
  )} in ${monthLabel}`;
});

const showGrowthPill = computed<boolean>(() => {
  if (showSkeleton.value) return false;
  if (!oldestBucket.value) return false;
  return oldestBucket.value.active_members > 0;
});

const labels = computed<string[]>(() =>
  (buckets.value ?? []).map((b) => moment(b.month, "YYYY-MM").format("MMM"))
);

const series = computed(() => {
  const data = buckets.value ?? [];
  return [
    {
      label: "Active",
      legendLabel: "Active",
      type: "line",
      data: data.map((b) => b.active_members),
      borderColor: ACTIVE_LINE_COLOR,
      backgroundColor: "transparent",
      pointBackgroundColor: ACTIVE_LINE_COLOR,
      pointBorderColor: ACTIVE_LINE_COLOR,
      fill: false,
      lineTension: 0.35,
      borderWidth: 2,
      pointRadius: 2,
      pointHoverRadius: 4,
      yAxisID: "y-active"
    },
    {
      label: "New",
      legendLabel: "Adds",
      type: "bar",
      data: data.map((b) => b.new_members),
      backgroundColor: NEW_BAR_COLOR,
      borderColor: NEW_BAR_COLOR,
      barPercentage: 0.55,
      categoryPercentage: 0.85,
      yAxisID: "y-flow"
    },
    {
      label: "Lost",
      legendLabel: "Losses",
      type: "bar",
      data: data.map((b) => b.lost_members),
      backgroundColor: LOST_BAR_COLOR,
      borderColor: LOST_BAR_COLOR,
      barPercentage: 0.55,
      categoryPercentage: 0.85,
      yAxisID: "y-flow"
    }
  ];
});

const options = computed(() => ({
  layout: { padding: { top: 8, right: 0, bottom: 0, left: 0 } },
  legend: { display: false },
  scales: {
    xAxes: [
      {
        gridLines: { display: false, drawBorder: false },
        ticks: {
          fontSize: 11,
          fontColor: "#6B7280",
          autoSkipPadding: 12,
          maxRotation: 0,
          minRotation: 0
        }
      }
    ],
    yAxes: [
      {
        id: "y-active",
        position: "left",
        display: false,
        ticks: { beginAtZero: false, precision: 0 }
      },
      {
        id: "y-flow",
        position: "right",
        ticks: {
          beginAtZero: true,
          precision: 0,
          fontSize: 11,
          fontColor: "#6B7280",
          maxTicksLimit: 5,
          padding: 4
        },
        gridLines: {
          color: "#F3F4F6",
          zeroLineColor: "#E5E7EB",
          drawBorder: false
        }
      }
    ]
  }
}));

function formatYTick(value: number): string {
  return value.toLocaleString("en-US");
}

function monthRangeFor(monthString: string): string {
  const start = moment(monthString, "YYYY-MM").startOf("month");
  const end = start.clone().endOf("month");
  return `${start.format("MM/DD/YYYY")}-${end.format("MM/DD/YYYY")}`;
}

function membersHrefFor(
  filterKey: "starts_on" | "ends_on",
  monthString: string
): string | null {
  if (selectedProgramIds.value.length > 1) return null;
  const range = monthRangeFor(monthString);
  // For New, the report's `starts_on` filter pairs with the customer-level
  // `first_ever_membership` flag. For Lost, we use the customer-level
  // `customer_ended_within` filter (not the row-level `ends_on`) so the
  // distinct-customer count matches the tile when DISTINCT-ON in the
  // report's base scope would otherwise drop customers whose representative
  // UMP ended outside the window.
  const reportParam =
    filterKey === "starts_on" ? "starts_on" : "customer_ended_within";
  let href = `/reports/members?${reportParam}=${range}`;
  const dashboardAlignedFlag =
    filterKey === "starts_on"
      ? "first_ever_membership"
      : "no_current_membership_in_scope";
  href += `&${dashboardAlignedFlag}=true`;
  if (selectedProgramIds.value.length === 1) {
    href += `&program=${selectedProgramIds.value[0]}`;
  }
  // Suppress the Members report's default Membership Status filter — see
  // useMembershipDashboard.membersReportHref for the contract.
  href += `&from_dashboard=1`;
  return href;
}

interface PointClickPayload {
  dataset: { label?: string };
  pointIndex: number;
}

function handlePointClick({ dataset, pointIndex }: PointClickPayload) {
  const data = buckets.value;
  if (!data || data[pointIndex] === undefined) return;
  const monthString = data[pointIndex].month;

  let href: string | null = null;
  if (dataset.label === "New") {
    href = membersHrefFor("starts_on", monthString);
  } else if (dataset.label === "Lost") {
    href = membersHrefFor("ends_on", monthString);
  }
  // Active line points are non-clickable in v1 (no `as_of` filter exists).

  if (href) window.location.href = href;
}

onMounted(() => {
  store.dispatch("fetchMembershipDashboardGrowthChart");
});
</script>

<style lang="scss" scoped>
.growth-chart {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background-color: #fff;
  border: 1px solid var(--medium-color, #e5e7eb);
  border-radius: 6px;
  padding: 1.25rem;
  height: 100%;
  min-height: 22rem;
}
.growth-chart__head {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
}
.growth-chart__heading {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}
.growth-chart__eyebrow {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #4b5563;
}
.growth-chart__title-row {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}
.growth-chart__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
}
.growth-chart__info {
  font-size: 0.75rem;
  color: #9ca3af;
  cursor: help;
  transition: color 120ms ease;
  &:hover {
    color: #4b5563;
  }
}
.growth-chart__tooltip-text {
  font-size: 0.8125rem;
  line-height: 1.4;
}
.growth-chart__growth-pill-wrap {
  display: inline-flex;
  align-items: center;
  margin-left: 0.25rem;
}
.growth-chart__growth-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
  letter-spacing: 0.01em;
  border-radius: 999px;
  cursor: help;
  white-space: nowrap;
}
.growth-chart__growth-pill--positive {
  color: #047857;
  background-color: rgba(4, 120, 87, 0.08);
}
.growth-chart__growth-pill--negative {
  color: #b91c1c;
  background-color: rgba(185, 28, 28, 0.08);
}
.growth-chart__growth-pill--neutral {
  color: #6b7280;
  background-color: rgba(107, 114, 128, 0.08);
}
.growth-chart__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.875rem;
  margin: 0;
  padding: 0;
  list-style: none;
}
.growth-chart__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
}
.growth-chart__swatch {
  display: inline-block;
  flex-shrink: 0;
}
.growth-chart__swatch--bar {
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 1px;
}
.growth-chart__swatch--line {
  width: 0.875rem;
  height: 2px;
  border-radius: 999px;
}
.growth-chart__legend-label {
  font-size: 0.75rem;
  color: #4b5563;
  letter-spacing: 0.02em;
}
.growth-chart__body {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.growth-chart__canvas-wrap {
  position: relative;
  flex: 1;
  min-height: 16rem;
}

@media (min-width: 640px) {
  .growth-chart__head {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
  }
}
</style>
